import type { TemplatePage } from '../templates';

const filled = [
  { item: 'Patio slab, 16 x 20 ft (320 sq ft), 4 in thick, 4,000 psi, fiber mesh', amount: '$3,200' },
  { item: 'Excavate 8 in, 4 in compacted crushed stone, forms and #3 rebar on 18 in centers', amount: '$1,150' },
  { item: 'Broom finish with tooled edges; saw-cut control joints at 8 ft spacing', amount: '$380' },
  { item: 'Optional: stamped ashlar slate pattern with release color instead of broom finish', amount: '$1,600' },
];

const faq = [
  {
    q: 'What should a concrete estimate include?',
    a: 'The dimensions and thickness, the mix and any fiber or reinforcement, the base and how it is compacted, the finish and joint spacing, removal of old material, access for the truck or a pump, curing time, and your terms on cracking, weather and payment.',
  },
  {
    q: 'Should I promise that the concrete will not crack?',
    a: 'Most contractors do not, because concrete can crack even when it is placed well. The template has a blank for a plain statement of what is normal and what your workmanship terms cover.',
  },
  {
    q: 'Is this concrete estimate template free?',
    a: 'Yes. Read it here, download the PDF, or copy the sections you need. Nothing to sign up for.',
  },
  {
    q: 'Can I send the estimate for an online signature?',
    a: 'In Scopie the same estimate is emailed to the customer as a link with your company name; they can tick an upgrade such as a stamped finish, read your terms and sign before you book the pour.',
  },
];

const page: TemplatePage = {
  slug: 'concrete-estimate-template',
  path: '/templates/concrete-estimate-template',
  primaryKeyword: 'concrete estimate template',
  secondaryKeywords: ['concrete quote template', 'concrete driveway estimate form', 'flatwork estimate template'],
  title: 'Free Concrete Estimate Template (PDF + Example) | Scopie', // 55
  metaDescription:
    'A free concrete estimate template for driveways, patios and walks: size, thickness, mix, base, steel, finish, joints, pour access, cure and cracking terms.', // 153
  h1: 'Concrete estimate template',
  answerFirst:
    'A concrete estimate should give the slab size and thickness, the mix, the base and reinforcement, the finish and joint layout, and how the truck reaches the pour. Then it needs plain terms on curing, weather and cracking. This free template covers residential flatwork, with a patio example that offers a stamped finish as an upgrade.',
  lastReviewed: '2026-10-07',
  schemaType: 'DigitalDocument',
  leadSheet: true,
  blocks: [
    {
      type: 'document',
      id: 'template',
      h2: 'The concrete estimate',
      intro: 'One section per slab if the job has more than one (driveway, walk, patio).',
      pdfName: 'concrete-estimate-template',
      sections: [
        { heading: 'Contractor', lines: ['[Company] · [Phone] · [Email] · [License number, if required]'] },
        { heading: 'Customer', lines: ['[Name] · [Address]', 'Estimate [number] · [date] · valid until [date]'] },
        {
          heading: 'Slab',
          lines: [
            '[Driveway / patio / walk / pad]: [length] x [width] = [sq ft] · thickness [inches]',
            'Mix [psi], [fiber / no fiber] · reinforcement [rebar size and spacing / wire mesh / none]',
            'Base: excavate [inches], [crushed stone / gravel] [inches] compacted',
            'Finish [broom / exposed aggregate / stamped pattern and color] · control joints every [ft]',
          ],
        },
        { heading: 'Removal and site work', lines: ['Remove [asphalt / concrete / sod], [sq ft], haul away · grading for drainage toward [direction]'] },
        { heading: 'Access', lines: ['Truck access to the pour [direct / pump / buggies] · street permit for the apron [yes / no], obtained by [us / owner]'] },
        {
          heading: 'Terms',
          lines: [
            'Curing: foot traffic after [days]; vehicles after [days].',
            'Cracking: [your statement about normal cracking and your workmanship terms].',
            'Weather: the pour date moves for rain or freezing temperatures; the price does not.',
            'Deposit: [amount] on acceptance. Cancellation within [time] of the pour: [your terms].',
          ],
        },
        { heading: 'Signature', lines: ['[Customer name] · [Signature] · [Date]'] },
      ],
    },
    {
      type: 'sample-quote',
      id: 'filled',
      h2: 'A backyard patio, filled in',
      label: 'Example: 320 sq ft patio',
      lines: filled,
      total: '$6,330',
      totalLabel: 'Total if the stamped finish is chosen',
      note: 'Illustrative figures for one project.',
    },
    {
      type: 'list',
      id: 'tips',
      h2: 'Details that save a concrete job',
      style: 'bullets',
      items: [
        { title: 'Thickness by use', text: 'A walk, a patio and a driveway carrying a pickup do not need the same slab. Write the thickness so it is never assumed.' },
        { title: 'Drainage direction', text: 'Water pooling against a foundation is the complaint that comes back. Note the slope.' },
        { title: 'Pump or no pump', text: 'If the truck cannot reach, a pump or buggies change the cost. Decide at the site visit.' },
        { title: 'Upgrades as options', text: 'Stamped or colored finishes are easier to sell as an optional line next to the standard finish.' },
      ],
    },
    {
      type: 'list',
      id: 'mistakes',
      h2: 'Estimate mistakes on flatwork',
      style: 'mistakes',
      items: [
        { title: 'No base depth', text: 'The base is invisible on day one and decisive in year three.' },
        { title: 'Joint spacing left out', text: 'Joints decide where cracks go; customers notice when they are missing.' },
        { title: 'Silence on pour-day cancellation', text: 'Ready-mix and crews are booked for that morning.' },
      ],
    },
    { type: 'faq', id: 'faq', h2: 'Concrete estimate template FAQ', items: faq },
  ],
  faq,
  internalLinks: [
    { href: '/for/concrete', anchor: 'Scopie for concrete contractors', status: 'live' },
    { href: '/templates/fence-estimate-template', anchor: 'fence estimate template', status: 'live' },
    { href: '/templates', anchor: 'All free templates', status: 'live' },
  ],
  cta: {
    heading: 'Book the pour once it is signed',
    text: 'Save your slab, base and finish items in Scopie, and email each concrete estimate from the curb.',
    button: 'Start free',
    href: '/register',
    fine: '14-day free trial. No credit card required.',
  },
};

export default page;
